const THREE = window.THREE;
const Autodesk = window.Autodesk;
import { drawLine } from '@/utils/threejsUtils';
import { removeFromScene, addToScene } from '@/utils/forgeUtils';
import { isPointInPolygon } from '../utils/zoneGeometry';

const SCENE = 'zone-draw-2d';
const CLICK_TOLERANCE_PX = 4; // more movement than this between down and up is a pan, not a click
const CLOSE_SNAP_PX = 12; // clicking this close to the first point closes the shape
const HANDLE_PX = 12;

const toColourInt = (hex) => parseInt((hex || '#2cc295').slice(1, 7), 16);
const isTyping = (target) => /^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName) || target?.isContentEditable;

/**
 * Polygon drawing on a 2D plan for zone boundaries. Same interaction as the plan's Measure Area
 * tool (Plan2DExtension): click to add points, click the first point or press Enter to close,
 * Esc to clear. On top of it: visible control points that can be dragged once the shape is
 * closed, dragging inside the closed shape moves it as a whole, Backspace removes the last point,
 * and the label shows the zone name instead of the area.
 *
 * Works in plan (sheet) coordinates; the caller maps them to world metres with the level mapping.
 * Plan2DExtension itself is not used because the admin plan viewer does not load it and its
 * load() adds tag icons, the Add Tag menu and panorama listeners.
 */
export class ZonePlanDrawTool {
  /**
   * @param {Autodesk.Viewing.Viewer3D} viewer - the plan viewer
   * @param {object} options
   * @param {string} options.label - zone name shown on the shape
   * @param {string} options.colour - zone colour, #RRGGBB
   * @param {THREE.Vector3[]} [options.points] - an existing closed shape to edit
   * @param {(points: THREE.Vector3[]) => void} options.onChange - called with the closed shape
   * @param {(state: 'drawing'|'closed') => void} [options.onState]
   */
  constructor(viewer, { label, colour, points, onChange, onState }) {
    this.viewer = viewer;
    this.label = label;
    this.colour = colour;
    this.onChange = onChange;
    this.onState = onState || (() => {});
    this.points = points?.length >= 3 ? points.map((p) => new THREE.Vector3(p.x, p.y, 0)) : [];
    this.closed = this.points.length >= 3;
    this.preview = null; // the moving point while drawing
    this.meshes = [];
    this.handles = [];
    this.labelEl = null;
    this.downAt = null;

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onShapePointerDown = this.onShapePointerDown.bind(this);
    this.endShapeDrag = null;
    this.layout = this.layout.bind(this);

    const canvas = viewer.canvas;
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mouseup', this.onMouseUp);
    // Capture phase on the container runs before the viewer's navigation sees the press.
    viewer.container.addEventListener('pointerdown', this.onShapePointerDown, true);
    document.addEventListener('keydown', this.onKeyDown);
    viewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.layout);
    canvas.style.cursor = this.closed ? '' : 'crosshair';

    this.render();
    this.onState(this.closed ? 'closed' : 'drawing');
  }

  setLabel(label) {
    this.label = label;
    if (this.labelEl) this.labelEl.textContent = label;
  }

  setColour(colour) {
    this.colour = colour;
    this.render();
  }

  // Clears the shape and starts drawing again.
  restart() {
    this.points = [];
    this.preview = null;
    this.closed = false;
    this.viewer.canvas.style.cursor = 'crosshair';
    this.render();
    this.onState('drawing');
  }

  destroy() {
    const canvas = this.viewer.canvas;
    canvas?.removeEventListener('mousedown', this.onMouseDown);
    canvas?.removeEventListener('mousemove', this.onMouseMove);
    canvas?.removeEventListener('mouseup', this.onMouseUp);
    this.viewer.container?.removeEventListener('pointerdown', this.onShapePointerDown, true);
    this.endShapeDrag?.();
    document.removeEventListener('keydown', this.onKeyDown);
    this.viewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.layout);
    if (canvas) canvas.style.cursor = '';
    this.clearMeshes();
    this.clearDom();
    this.viewer.impl?.invalidate(false, false, true);
  }

  // --- input -------------------------------------------------------------------------------

  toPlan(clientX, clientY) {
    const rect = this.viewer.canvas.getBoundingClientRect();
    const hit = this.viewer.clientToWorld(clientX - rect.left, clientY - rect.top, true, true);
    return hit?.point ? new THREE.Vector3(hit.point.x, hit.point.y, 0) : null;
  }

  toScreen(point) {
    return this.viewer.worldToClient(new THREE.Vector3(point.x, point.y, 0));
  }

  isNearFirstPoint(clientX, clientY) {
    if (!this.points.length) return false;
    const rect = this.viewer.canvas.getBoundingClientRect();
    const first = this.toScreen(this.points[0]);
    return Math.hypot(clientX - rect.left - first.x, clientY - rect.top - first.y) <= CLOSE_SNAP_PX;
  }

  onMouseDown(event) {
    if (event.button === 0) this.downAt = { x: event.clientX, y: event.clientY };
  }

  isInsideShape(clientX, clientY) {
    const point = this.toPlan(clientX, clientY);
    return !!point && isPointInPolygon(point, this.points);
  }

  onMouseMove(event) {
    if (this.closed) {
      if (!this.endShapeDrag) this.viewer.canvas.style.cursor = this.isInsideShape(event.clientX, event.clientY) ? 'move' : '';
      return;
    }
    if (!this.points.length) return;
    this.preview = this.isNearFirstPoint(event.clientX, event.clientY) ? this.points[0].clone() : this.toPlan(event.clientX, event.clientY);
    this.render();
  }

  onMouseUp(event) {
    const down = this.downAt;
    this.downAt = null;
    if (this.closed || event.button !== 0 || !down) return;
    if (Math.hypot(event.clientX - down.x, event.clientY - down.y) > CLICK_TOLERANCE_PX) return;
    if (this.points.length >= 3 && this.isNearFirstPoint(event.clientX, event.clientY)) {
      this.close();
      return;
    }
    const point = this.toPlan(event.clientX, event.clientY);
    if (!point) return;
    this.points.push(point);
    this.preview = point.clone();
    this.render();
  }

  onKeyDown(event) {
    if (isTyping(event.target)) return;
    if (event.key === 'Enter' && !this.closed) {
      this.close();
    } else if (event.key === 'Escape' && !this.closed) {
      this.restart();
    } else if (event.key === 'Backspace' && !this.closed && this.points.length) {
      event.preventDefault();
      this.points.pop();
      if (!this.points.length) this.preview = null;
      this.render();
    }
  }

  close() {
    if (this.points.length < 3) return;
    this.closed = true;
    this.preview = null;
    this.viewer.canvas.style.cursor = '';
    this.render();
    this.onState('closed');
    this.onChange(this.points.map((p) => p.clone()));
  }

  // Pressing inside the closed shape moves the whole shape instead of panning the plan.
  onShapePointerDown(event) {
    if (!this.closed || event.button !== 0 || event.target !== this.viewer.canvas) return;
    const start = this.toPlan(event.clientX, event.clientY);
    if (!start || !isPointInPolygon(start, this.points)) return;
    // preventDefault also suppresses the mousedown the browser would send after this pointerdown.
    event.preventDefault();
    event.stopPropagation();
    const origin = this.points.map((p) => p.clone());
    let moved = false;
    const move = (e) => {
      const point = this.toPlan(e.clientX, e.clientY);
      if (!point) return;
      const dx = point.x - start.x;
      const dy = point.y - start.y;
      this.points.forEach((p, i) => p.set(origin[i].x + dx, origin[i].y + dy, 0));
      moved = true;
      this.renderMeshes();
      this.layout();
    };
    const end = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      this.endShapeDrag = null;
      if (moved) this.onChange(this.points.map((p) => p.clone()));
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    this.endShapeDrag = end;
  }

  // Control points: dragged with pointer capture so the viewer never sees the gesture as a pan.
  startHandleDrag(index, event) {
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const move = (e) => {
      const point = this.toPlan(e.clientX, e.clientY);
      if (!point) return;
      // Move in place: rebuilding the handles would drop the pointer capture mid-drag.
      this.points[index].set(point.x, point.y, 0);
      this.renderMeshes();
      this.layout();
    };
    const end = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      this.onChange(this.points.map((p) => p.clone()));
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  }

  // --- drawing -----------------------------------------------------------------------------

  outline() {
    if (this.closed) return [...this.points, this.points[0]];
    return this.preview ? [...this.points, this.preview] : [...this.points];
  }

  render() {
    this.renderMeshes();
    this.renderDom();
  }

  renderMeshes() {
    this.clearMeshes();
    const colour = toColourInt(this.colour);
    const outline = this.outline();
    for (let i = 0; i < outline.length - 1; i++) {
      this.meshes.push(drawLine(outline[i], outline[i + 1], colour, this.viewer, SCENE));
    }
    if (outline.length >= 3) this.meshes.push(this.drawFill(outline, colour));
    this.viewer.impl.invalidate(false, false, true);
  }

  drawFill(outline, colour) {
    const shape = new THREE.Shape();
    shape.moveTo(outline[0].x, outline[0].y);
    outline.slice(1).forEach((p) => shape.lineTo(p.x, p.y));
    const mesh = new THREE.Mesh(
      new THREE.ShapeGeometry(shape),
      new THREE.MeshBasicMaterial({ color: colour, depthTest: false, depthWrite: false, transparent: true, opacity: 0.2 }),
    );
    addToScene(mesh, this.viewer, SCENE);
    return mesh;
  }

  renderDom() {
    this.clearDom();
    const container = this.viewer.container;
    if (!container) return;
    this.points.forEach((point, index) => {
      const handle = document.createElement('div');
      const isCloseTarget = !this.closed && index === 0 && this.points.length >= 3;
      const size = isCloseTarget ? HANDLE_PX + 4 : HANDLE_PX;
      Object.assign(handle.style, {
        position: 'absolute',
        width: `${size}px`,
        height: `${size}px`,
        marginLeft: `${-size / 2}px`,
        marginTop: `${-size / 2}px`,
        borderRadius: '50%',
        background: isCloseTarget ? this.colour : '#ffffff',
        border: `2px solid ${this.colour}`,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
        boxSizing: 'border-box',
        zIndex: 2,
        // While drawing, clicks must reach the canvas (the first point closes via its snap radius).
        pointerEvents: this.closed ? 'auto' : 'none',
        cursor: this.closed ? 'move' : 'default',
        touchAction: 'none',
      });
      handle.title = this.closed ? `Point ${index + 1}: drag to move` : '';
      if (this.closed) handle.addEventListener('pointerdown', (event) => this.startHandleDrag(index, event));
      container.appendChild(handle);
      this.handles.push({ el: handle, point });
    });

    if (this.points.length >= 3 || (this.points.length >= 2 && this.preview)) {
      const label = document.createElement('div');
      label.textContent = this.label;
      Object.assign(label.style, {
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        background: this.colour,
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: '600',
        padding: '4px 10px',
        borderRadius: '10px',
        whiteSpace: 'nowrap',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.35)',
        pointerEvents: 'none',
        zIndex: 1,
      });
      container.appendChild(label);
      this.labelEl = label;
    }
    this.layout();
  }

  layout() {
    this.handles.forEach(({ el, point }) => {
      const screen = this.toScreen(point);
      el.style.left = `${screen.x}px`;
      el.style.top = `${screen.y}px`;
    });
    if (this.labelEl) {
      // Same anchor as the Measure Area label: the centre of the shape's bounds.
      const centre = new THREE.Box2().setFromPoints(this.outline()).getCenter();
      const screen = this.toScreen(centre);
      this.labelEl.style.left = `${screen.x}px`;
      this.labelEl.style.top = `${screen.y}px`;
    }
  }

  clearMeshes() {
    removeFromScene(this.meshes, this.viewer, SCENE);
    this.meshes = [];
  }

  clearDom() {
    this.handles.forEach(({ el }) => el.remove());
    this.handles = [];
    this.labelEl?.remove();
    this.labelEl = null;
  }
}
